import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { doc, getDoc, setDoc, serverTimestamp, collection, getDocs } from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';

// ── Cloudinary upload helper ──────────────────────────────────────────────
const CLOUD_NAME = 'YOUR_CLOUD_NAME'; // ← badilisha hii
const UPLOAD_PRESET = 'YOUR_UPLOAD_PRESET'; // ← badilisha hii (unsigned preset)

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  const res = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, {
    method: 'POST',
    body: formData,
  });
  const data = await res.json();
  if (!data.secure_url) throw new Error('Upload failed');
  return data.secure_url;
}

export default function PaymentSettings() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('payment');
  const [donationSubTab, setDonationSubTab] = useState('students');

  // Payment form
  const [form, setForm] = useState({
    bankName: '', accountName: '', accountNumber: '', branchName: '', swiftCode: '',
    mpesaNumber: '', mpesaName: '', tigoPesaNumber: '', tigoPesaName: '',
    airtelNumber: '', airtelName: '', halotelNumber: '', halotelName: '',
    paypalEmail: '', otherMethod: '', otherDetails: '', instructions: '',
  });

  // Students & parents from their existing collections
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [uploadingId, setUploadingId] = useState(null); // track which card is uploading
  const [photoOverrides, setPhotoOverrides] = useState({}); // { docId: cloudinaryUrl }

  useEffect(() => { fetchAll(); }, []);

  async function fetchAll() {
    try {
      // Load payment settings
      const settingsSnap = await getDoc(doc(db, 'settings', 'payments'));
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        if (data.bankName !== undefined) {
          setForm({
            bankName: data.bankName || '', accountName: data.accountName || '',
            accountNumber: data.accountNumber || '', branchName: data.branchName || '',
            swiftCode: data.swiftCode || '', mpesaNumber: data.mpesaNumber || '',
            mpesaName: data.mpesaName || '', tigoPesaNumber: data.tigoPesaNumber || '',
            tigoPesaName: data.tigoPesaName || '', airtelNumber: data.airtelNumber || '',
            airtelName: data.airtelName || '', halotelNumber: data.halotelNumber || '',
            halotelName: data.halotelName || '', paypalEmail: data.paypalEmail || '',
            otherMethod: data.otherMethod || '', otherDetails: data.otherDetails || '',
            instructions: data.instructions || '',
          });
        }
        // Load saved photo overrides
        if (data.photoOverrides) setPhotoOverrides(data.photoOverrides);
      }

      // Load existing students & parents collections
      const [studentsSnap, parentsSnap] = await Promise.all([
        getDocs(collection(db, 'students')),
        getDocs(collection(db, 'parents')),
      ]);
      setStudents(studentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setParents(parentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
  }

  function handleChange(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function handlePhotoUpload(docId, file) {
    if (!file) return;
    setUploadingId(docId);
    try {
      const url = await uploadToCloudinary(file);
      const updated = { ...photoOverrides, [docId]: url };
      setPhotoOverrides(updated);
      // Save immediately to Firebase
      await setDoc(doc(db, 'settings', 'payments'), {
        ...form, photoOverrides: updated, updatedAt: serverTimestamp(),
      }, { merge: true });
      setSuccess('Picha imehifadhiwa!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Upload imeshindwa. Jaribu tena.');
      setTimeout(() => setError(''), 3000);
    }
    setUploadingId(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true); setError(''); setSuccess('');
    try {
      await setDoc(doc(db, 'settings', 'payments'), {
        ...form, photoOverrides, updatedAt: serverTimestamp(),
      });
      setSuccess('Settings saved successfully!');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  }

  // Get photo: override first, then existing record photo, then null
  function getPhoto(person) {
    return photoOverrides[person.id]
      || person.photoURL
      || person.photo
      || person.imageUrl
      || null;
  }

  function getStudentName(s) {
    return s.name || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Mwanafunzi';
  }
  function getParentName(p) {
    return p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Mzazi';
  }

  // ── Photo card ─────────────────────────────────────────────────────────
  function PhotoCard({ person, nameLabel, metaLabel, type }) {
    const photo = getPhoto(person);
    const isUploading = uploadingId === person.id;

    return (
      <div style={st.photoCard}>
        {/* Photo */}
        <div style={st.photoWrap}>
          {photo
            ? <img src={photo} alt={nameLabel} style={st.photoImg} />
            : <div style={st.photoPlaceholder}>{type === 'student' ? '🏾' : '🏾'}</div>}
          {isUploading && (
            <div style={st.uploadingOverlay}>
              <div style={st.uploadingSpinner}></div>
            </div>
          )}
        </div>
        {/* Info */}
        <div style={st.photoCardInfo}>
          <div style={st.photoCardName}>{nameLabel}</div>
          <div style={st.photoCardMeta}>{metaLabel}</div>
        </div>
        {/* Upload button */}
        <label style={st.uploadLabel}>
          {isUploading ? 'Inapakia...' : 'Badilisha Picha'}
          <input
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            disabled={isUploading}
            onChange={e => handlePhotoUpload(person.id, e.target.files[0])}
          />
        </label>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Payment Settings</div>
            <div style={styles.pageSub}>Manage payment details and donation photos for donors</div>
          </div>
        </div>

        {success && <div style={styles.successMsg}>{success}</div>}
        {error && <div style={styles.errorMsg}>{error}</div>}

        <div style={styles.tabs}>
          <button style={{ ...styles.tab, ...(activeTab === 'payment' ? styles.tabActive : {}) }} onClick={() => setActiveTab('payment')}>Payment Details</button>
          <button style={{ ...styles.tab, ...(activeTab === 'items' ? styles.tabActive : {}) }} onClick={() => setActiveTab('items')}>Donation Photos</button>
        </div>

        <form onSubmit={handleSubmit}>

          {/* PAYMENT DETAILS — unchanged */}
          {activeTab === 'payment' && (
            <div>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Bank Account Details</div>
                <div style={styles.formGrid}>
                  {[['bankName','Bank Name','e.g. CRDB Bank'],['accountName','Account Name','e.g. Iyanna Foundation'],['accountNumber','Account Number','e.g. 0150XXXXXXXXX'],['branchName','Branch Name','e.g. Kinondoni Branch'],['swiftCode','Swift Code','e.g. CRDBTZTZ']].map(([name,label,ph]) => (
                    <div key={name} style={styles.formGroup}><label style={styles.formLabel}>{label}</label><input style={styles.formInput} name={name} value={form[name]} onChange={handleChange} placeholder={ph} /></div>
                  ))}
                </div>
              </div>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Mobile Money</div>
                <div style={styles.formGrid}>
                  {[['mpesaNumber','M-Pesa Number'],['mpesaName','M-Pesa Name'],['tigoPesaNumber','Tigo Pesa Number'],['tigoPesaName','Tigo Pesa Name'],['airtelNumber','Airtel Money Number'],['airtelName','Airtel Money Name'],['halotelNumber','Halotel Number'],['halotelName','Halotel Name']].map(([name,label]) => (
                    <div key={name} style={styles.formGroup}><label style={styles.formLabel}>{label}</label><input style={styles.formInput} name={name} value={form[name]} onChange={handleChange} placeholder="+255 7XX XXX XXX" /></div>
                  ))}
                </div>
              </div>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>PayPal</div>
                <div style={styles.formGrid}><div style={styles.formGroup}><label style={styles.formLabel}>PayPal Email</label><input style={styles.formInput} name="paypalEmail" type="email" value={form.paypalEmail} onChange={handleChange} placeholder="donate@iyanna.org" /></div></div>
              </div>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Other Method</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}><label style={styles.formLabel}>Method Name</label><input style={styles.formInput} name="otherMethod" value={form.otherMethod} onChange={handleChange} placeholder="e.g. Western Union" /></div>
                  <div style={styles.formGroup}><label style={styles.formLabel}>Details</label><input style={styles.formInput} name="otherDetails" value={form.otherDetails} onChange={handleChange} placeholder="e.g. Send to Iyanna Foundation Tanzania" /></div>
                </div>
              </div>
              <div style={styles.panel}>
                <div style={styles.sectionTitle}>Instructions to Donors</div>
                <div style={styles.formGroup}><label style={styles.formLabel}>Message to donors</label><textarea style={{ ...styles.formInput, height: '100px', resize: 'vertical' }} name="instructions" value={form.instructions} onChange={handleChange} placeholder="e.g. After sending your donation please contact us..." /></div>
              </div>
            </div>
          )}

          {/* DONATION PHOTOS TAB */}
          {activeTab === 'items' && (
            <div>
              <div style={styles.panel}>
                <div style={st.infoBox}>
                  <div style={st.infoTitle}>Picha za Donors</div>
                  <div style={st.infoText}>
                    Watoto na wazazi tayari wana akaunti kwenye mfumo. Hapa unaweza tu kubadilisha picha zao ambazo zitaonekana kwa donors. Bonyeza "Badilisha Picha" kwa mtu yeyote.
                  </div>
                </div>

                {/* Sub-tabs */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                  <button type="button" style={{ ...styles.tab, ...(donationSubTab === 'students' ? styles.tabActive : {}) }} onClick={() => setDonationSubTab('students')}>
                    Wanafunzi ({students.length})
                  </button>
                  <button type="button" style={{ ...styles.tab, ...(donationSubTab === 'parents' ? styles.tabActive : {}) }} onClick={() => setDonationSubTab('parents')}>
                    Wazazi ({parents.length})
                  </button>
                </div>

                {/* STUDENTS */}
                {donationSubTab === 'students' && (
                  students.length === 0
                    ? <div style={st.empty}>Hakuna wanafunzi kwenye mfumo bado.</div>
                    : <div style={st.photoGrid}>
                        {students.map(s => (
                          <PhotoCard key={s.id} person={s}
                            nameLabel={getStudentName(s)}
                            metaLabel={s.grade || s.class || s.level || 'Mwanafunzi'}
                            type="student" />
                        ))}
                      </div>
                )}

                {/* PARENTS */}
                {donationSubTab === 'parents' && (
                  parents.length === 0
                    ? <div style={st.empty}>Hakuna wazazi kwenye mfumo bado.</div>
                    : <div style={st.photoGrid}>
                        {parents.map(p => (
                          <PhotoCard key={p.id} person={p}
                            nameLabel={getParentName(p)}
                            metaLabel={p.occupation || p.business || p.businessType || 'Mzazi'}
                            type="parent" />
                        ))}
                      </div>
                )}
              </div>
            </div>
          )}

          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Saving...' : 'Save All Settings'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Photo section styles ───────────────────────────────────────────────────
const st = {
  infoBox: { background: 'linear-gradient(135deg, #EAF3EC, #F1F8E9)', border: '1px solid #BBD8C4', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px' },
  infoTitle: { fontWeight: '700', color: '#14452F', fontSize: '14px', marginBottom: '5px' },
  infoText: { fontSize: '12.5px', color: '#388E3C', lineHeight: '1.6' },
  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '16px' },
  photoCard: { background: '#FAF9F5', border: '1.5px solid #E5E7EB', borderRadius: '14px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  photoWrap: { position: 'relative', width: '100%', height: '160px', background: 'linear-gradient(135deg, #EAF3EC, #d4e8f0)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoPlaceholder: { fontSize: '3.5rem' },
  uploadingOverlay: { position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  uploadingSpinner: { fontSize: '2rem' },
  photoCardInfo: { padding: '10px 12px 6px' },
  photoCardName: { fontWeight: '700', fontSize: '13px', color: '#1C1C1E', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  photoCardMeta: { fontSize: '11.5px', color: '#14452F', fontWeight: '600' },
  uploadLabel: { display: 'block', margin: '8px 10px 10px', padding: '8px 0', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', borderRadius: '8px', textAlign: 'center', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '32px', color: '#9CA3AF', fontSize: '13px', border: '2px dashed #E5E7EB', borderRadius: '12px' },
};

// ── Page styles ────────────────────────────────────────────────────────────
const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#F6F4EE' },
  main: { flex: 1, padding: '26px', overflowY: 'auto' },
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap', gap: '14px' },
  pageTitle: { fontFamily: "'Fraunces', Georgia, serif", fontSize: '25px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  pageSub: { fontSize: '12.5px', color: '#6B7280', marginTop: '3px' },
  successMsg: { background: '#EAF3EC', border: '1px solid #BBD8C4', color: '#14452F', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  errorMsg: { background: '#FFEBEE', border: '1px solid #FFCDD2', color: '#C62828', padding: '12px 16px', borderRadius: '10px', fontSize: '13px', marginBottom: '20px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab: { padding: '10px 20px', borderRadius: '20px', border: '1.5px solid #E5E7EB', background: '#fff', color: '#6B7280', fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit', fontWeight: '500' },
  tabActive: { background: 'linear-gradient(135deg, #14452F, #1F6B45)', borderColor: 'transparent', color: '#fff' },
  panel: { background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.04)', marginBottom: '16px' },
  sectionTitle: { fontSize: '11px', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#6B7280', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid #F3F4F6', fontWeight: '600' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  formGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  formLabel: { fontSize: '12px', color: '#6B7280', fontWeight: '500' },
  formInput: { padding: '11px 14px', borderRadius: '9px', border: '1.5px solid #E5E7EB', background: '#FAF9F5', fontFamily: 'inherit', fontSize: '13.5px', color: '#1C1C1E', outline: 'none', width: '100%', boxSizing: 'border-box' },
  submitBtn: { width: '100%', padding: '15px', background: 'linear-gradient(135deg, #14452F, #1F6B45)', color: '#fff', border: 'none', borderRadius: '12px', fontFamily: 'inherit', fontSize: '15px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 6px 20px rgba(20,69,47,0.3)', marginBottom: '30px' },
};